"use strict";
/**
  * @reach/slider v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */


// src/reach-slider.tsx
import * as React from "react";
import { useId } from "@reach/auto-id";
import {
  composeEventHandlers,
  createContext,
  getOwnerDocument,
  isFunction,
  isRightClick,
  makeId,
  noop,
  useComposedRefs,
  useControlledState,
  useIsomorphicLayoutEffect as useLayoutEffect,
  useStableLayoutCallback
} from "@reach/utils";
var SliderOrientation = /* @__PURE__ */ ((SliderOrientation2) => {
  SliderOrientation2["Horizontal"] = "horizontal";
  SliderOrientation2["Vertical"] = "vertical";
  return SliderOrientation2;
})(SliderOrientation || {});
var SliderHandleAlignment = /* @__PURE__ */ ((SliderHandleAlignment2) => {
  SliderHandleAlignment2["Center"] = "center";
  SliderHandleAlignment2["Contain"] = "contain";
  return SliderHandleAlignment2;
})(SliderHandleAlignment || {});
var SLIDER_ORIENTATION_HORIZONTAL = "horizontal" /* Horizontal */;
var SLIDER_ORIENTATION_VERTICAL = "vertical" /* Vertical */;
var SLIDER_HANDLE_ALIGN_CENTER = "center" /* Center */;
var SLIDER_HANDLE_ALIGN_CONTAIN = "contain" /* Contain */;
var [SliderProvider, useSliderContext] = createContext("Slider");
var Slider = React.forwardRef(function Slider2({ children, ...props }, forwardedRef) {
  return /* @__PURE__ */ React.createElement(SliderInput, {
    ...props,
    ref: forwardedRef,
    "data-reach-slider": "",
    __componentName: "Slider"
  }, /* @__PURE__ */ React.createElement(SliderTrack2, null, /* @__PURE__ */ React.createElement(SliderRange2, null), /* @__PURE__ */ React.createElement(SliderHandle2, null), children));
});
Slider.displayName = "Slider";
var SliderInput = React.forwardRef(function SliderInput2({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-valuetext": ariaValueTextProp,
  as: Comp = "div",
  defaultValue,
  disabled = false,
  value: controlledValue,
  getAriaLabel,
  getAriaValueText,
  getValueText: DEPRECATED_getValueText,
  handleAlignment = "center" /* Center */,
  max = 100,
  min = 0,
  name,
  onChange,
  onKeyDown,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onPointerDown,
  onPointerUp,
  onTouchEnd,
  onTouchMove,
  onTouchStart,
  orientation = "horizontal" /* Horizontal */,
  step = 1,
  children,
  __componentName = "SliderInput",
  ...rest
}, forwardedRef) {
  if (true) {
    let depecratedPropIsSet = !!DEPRECATED_getValueText;
    React.useEffect(() => {
      if (depecratedPropIsSet) {
        console.warn("The `getValueText` prop in @reach/slider is deprecated. Please use `getAriaValueText` instead.");
      }
    }, [depecratedPropIsSet]);
  }
  let touchId = React.useRef();
  let id = useId(rest.id);
  let pointerDownRef = React.useRef(false);
  let trackRef = React.useRef(null);
  let handleRef = React.useRef(null);
  let sliderRef = React.useRef(null);
  let ref = useComposedRefs(sliderRef, forwardedRef);
  let [hasFocus, setHasFocus] = React.useState(false);
  let { ref: x, ...handleDimensions } = useDimensions(handleRef);
  defaultValue = defaultValue || min;
  let defaultValueRef = React.useRef(defaultValue);
  let isControlledRef = React.useRef(controlledValue !== void 0);
  let [_value, setValue] = useControlledState({
    controlledValue,
    defaultValue,
    calledFrom: "SliderInput"
  });
  let value = clamp(_value, min, max);
  let trackPercent = valueToPercent(value, min, max);
  let isVertical = orientation === "vertical" /* Vertical */;
  let handleSize = isVertical ? handleDimensions.height : handleDimensions.width;
  let handlePosition = `calc(${trackPercent}% - ${handleAlignment === "center" /* Center */ ? `${handleSize}px / 2` : `${handleSize}px * ${trackPercent * 0.01}`})`;
  let handlePositionRef = React.useRef(handlePosition);
  useLayoutEffect(() => {
    handlePositionRef.current = handlePosition;
  }, [handlePosition]);
  let onChangeRef = React.useRef(onChange);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  let updateValue = React.useCallback(function updateValue2(newValue) {
    setValue(newValue);
    if (onChangeRef.current) {
      onChangeRef.current(newValue, {
        min,
        max,
        handlePosition: handlePositionRef.current
      });
    }
  }, [max, min, setValue]);
  let getNewValueFromEvent = React.useCallback((event) => {
    return getNewValue(getPointerPosition(event, touchId), trackRef.current, {
      step,
      orientation,
      min,
      max
    });
  }, [max, min, orientation, step]);
  let handleKeyDown = useStableLayoutCallback((event) => {
    if (disabled) {
      return;
    }
    let newValue;
    let tenSteps = (max - min) / 10;
    let keyStep = step || (max - min) / 100;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        newValue = value - keyStep;
        break;
      case "ArrowRight":
      case "ArrowUp":
        newValue = value + keyStep;
        break;
      case "PageDown":
        newValue = value - tenSteps;
        break;
      case "PageUp":
        newValue = value + tenSteps;
        break;
      case "Home":
        newValue = min;
        break;
      case "End":
        newValue = max;
        break;
      default:
        return;
    }
    event.preventDefault();
    newValue = clamp(step ? roundValueToStep(newValue, step, min) : newValue, min, max);
    updateValue(newValue);
  });
  let ariaValueText = DEPRECATED_getValueText ? DEPRECATED_getValueText(value) : getAriaValueText ? getAriaValueText(value) : ariaValueTextProp;
  let rangeStyle = { [isVertical ? "height" : "width"]: `${trackPercent}%` };
  let removeMoveEvents = React.useRef(noop);
  let removeStartEvents = React.useRef(noop);
  let removeEndEvents = React.useRef(noop);
  let appEvents = React.useRef({
    onMouseMove,
    onMouseDown,
    onMouseUp,
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onPointerDown,
    onPointerUp
  });
  useLayoutEffect(() => {
    appEvents.current.onMouseMove = onMouseMove;
    appEvents.current.onMouseDown = onMouseDown;
    appEvents.current.onMouseUp = onMouseUp;
    appEvents.current.onTouchStart = onTouchStart;
    appEvents.current.onTouchEnd = onTouchEnd;
    appEvents.current.onTouchMove = onTouchMove;
    appEvents.current.onPointerDown = onPointerDown;
    appEvents.current.onPointerUp = onPointerUp;
  }, [onMouseMove, onMouseDown, onMouseUp, onTouchStart, onTouchEnd, onTouchMove, onPointerDown, onPointerUp]);
  let handleSlideStart = useStableLayoutCallback((event) => {
    if (isRightClick(event))
      return;
    if (disabled) {
      pointerDownRef.current = false;
      return;
    }
    let ownerDocument = getOwnerDocument(sliderRef.current);
    let ownerWindow = ownerDocument.defaultView || window;
    pointerDownRef.current = true;
    if (event.changedTouches) {
      event.preventDefault();
      let touch = event.changedTouches?.[0];
      if (touch != null) {
        touchId.current = touch.identifier;
      }
    }
    let newValue = getNewValueFromEvent(event);
    if (newValue == null) {
      return;
    }
    ownerWindow.requestAnimationFrame(() => handleRef.current?.focus());
    updateValue(newValue);
    removeMoveEvents.current = addMoveListener();
    removeEndEvents.current = addEndListener();
  });
  let setPointerCapture = useStableLayoutCallback((event) => {
    if (isRightClick(event))
      return;
    if (disabled) {
      pointerDownRef.current = false;
      return;
    }
    pointerDownRef.current = true;
    sliderRef.current?.setPointerCapture(event.pointerId);
  });
  let releasePointerCapture = useStableLayoutCallback((event) => {
    if (isRightClick(event))
      return;
    sliderRef.current?.releasePointerCapture(event.pointerId);
    pointerDownRef.current = false;
  });
  let handlePointerMove = useStableLayoutCallback((event) => {
    if (disabled || !pointerDownRef.current) {
      pointerDownRef.current = false;
      return;
    }
    let newValue = getNewValueFromEvent(event);
    if (newValue == null) {
      return;
    }
    updateValue(newValue);
  });
  let handleSlideStop = useStableLayoutCallback((event) => {
    if (isRightClick(event))
      return;
    pointerDownRef.current = false;
    let newValue = getNewValueFromEvent(event);
    if (newValue == null) {
      return;
    }
    touchId.current = void 0;
    removeMoveEvents.current();
    removeEndEvents.current();
  });
  let addMoveListener = React.useCallback(() => {
    let ownerDocument = getOwnerDocument(sliderRef.current);
    let touchListener = composeEventHandlers(appEvents.current.onTouchMove, handlePointerMove);
    let mouseListener = composeEventHandlers(appEvents.current.onMouseMove, handlePointerMove);
    ownerDocument.addEventListener("touchmove", touchListener);
    ownerDocument.addEventListener("mousemove", mouseListener);
    return () => {
      ownerDocument.removeEventListener("touchmove", touchListener);
      ownerDocument.removeEventListener("mousemove", mouseListener);
    };
  }, [handlePointerMove]);
  let addEndListener = React.useCallback(() => {
    let ownerDocument = getOwnerDocument(sliderRef.current);
    let ownerWindow = ownerDocument.defaultView || window;
    let pointerListener = composeEventHandlers(appEvents.current.onPointerUp, releasePointerCapture);
    let touchListener = composeEventHandlers(appEvents.current.onTouchEnd, handleSlideStop);
    let mouseListener = composeEventHandlers(appEvents.current.onMouseUp, handleSlideStop);
    if ("PointerEvent" in ownerWindow) {
      ownerDocument.addEventListener("pointerup", pointerListener);
    }
    ownerDocument.addEventListener("touchend", touchListener);
    ownerDocument.addEventListener("mouseup", mouseListener);
    return () => {
      if ("PointerEvent" in ownerWindow) {
        ownerDocument.removeEventListener("pointerup", pointerListener);
      }
      ownerDocument.removeEventListener("touchend", touchListener);
      ownerDocument.removeEventListener("mouseup", mouseListener);
    };
  }, [handleSlideStop, releasePointerCapture]);
  let addStartListener = React.useCallback(() => {
    let sliderElement = sliderRef.current;
    if (!sliderElement) {
      return noop;
    }
    let ownerDocument = getOwnerDocument(sliderElement);
    let ownerWindow = ownerDocument.defaultView || window;
    let touchListener = composeEventHandlers(appEvents.current.onTouchStart, handleSlideStart);
    let mouseListener = composeEventHandlers(appEvents.current.onMouseDown, handleSlideStart);
    let pointerListener = composeEventHandlers(appEvents.current.onPointerDown, setPointerCapture);
    sliderElement.addEventListener("touchstart", touchListener);
    sliderElement.addEventListener("mousedown", mouseListener);
    if ("PointerEvent" in ownerWindow) {
      sliderElement.addEventListener("pointerdown", pointerListener);
    }
    return () => {
      sliderElement.removeEventListener("touchstart", touchListener);
      sliderElement.removeEventListener("mousedown", mouseListener);
      if ("PointerEvent" in ownerWindow) {
        sliderElement.removeEventListener("pointerdown", pointerListener);
      }
    };
  }, [setPointerCapture, handleSlideStart]);
  React.useEffect(() => {
    removeStartEvents.current = addStartListener();
    return () => {
      removeStartEvents.current();
      removeEndEvents.current();
      removeMoveEvents.current();
    };
  }, [addStartListener]);
  let shouldRenderInput = name != null;
  let inputRef = React.useRef(null);
  React.useEffect(() => {
    let isControlled = isControlledRef.current;
    let defaultValue2 = defaultValueRef.current;
    let inputElement = inputRef.current;
    let form = inputElement?.form;
    if (!shouldRenderInput || !form || isControlled)
      return;
    function handleReset(event) {
      setValue(defaultValue2);
    }
    form.addEventListener("reset", handleReset);
    return () => {
      form?.removeEventListener("reset", handleReset);
    };
  }, [setValue, shouldRenderInput]);
  return /* @__PURE__ */ React.createElement(SliderProvider, {
    ariaLabel: getAriaLabel ? getAriaLabel(value) : ariaLabel,
    ariaLabelledBy,
    ariaValueText,
    handleDimensions,
    handleKeyDown,
    handlePosition,
    handleRef,
    hasFocus,
    onKeyDown,
    setHasFocus,
    sliderId: id,
    sliderMax: max,
    sliderMin: min,
    value,
    disabled: !!disabled,
    isVertical,
    orientation,
    trackPercent,
    trackRef,
    rangeStyle,
    updateValue
  }, /* @__PURE__ */ React.createElement(Comp, {
    ...rest,
    ref,
    "data-reach-slider-input": "",
    "data-disabled": disabled ? "" : void 0,
    "data-orientation": orientation,
    tabIndex: -1
  }, isFunction(children) ? children({
    hasFocus,
    id,
    max,
    min,
    value,
    ariaValueText,
    valueText: ariaValueText
  }) : children, shouldRenderInput ? /* @__PURE__ */ React.createElement("input", {
    type: "hidden",
    value,
    name,
    id: id && makeId("input", id),
    ref: inputRef
  }) : null));
});
SliderInput.displayName = "SliderInput";
var SliderTrackImpl = React.forwardRef(function SliderTrack({ as: Comp = "div", children, style = {}, ...props }, forwardedRef) {
  const { disabled, orientation, trackRef } = useSliderContext("SliderTrack");
  const ref = useComposedRefs(trackRef, forwardedRef);
  return /* @__PURE__ */ React.createElement(Comp, {
    ref,
    style: { ...style, position: "relative" },
    ...props,
    "data-reach-slider-track": "",
    "data-disabled": disabled ? "" : void 0,
    "data-orientation": orientation
  }, children);
});
SliderTrackImpl.displayName = "SliderTrack";
var SliderTrack2 = React.memo(SliderTrackImpl);
SliderTrack2.displayName = "SliderTrack";
var SliderRangeImpl = React.forwardRef(function SliderRange({ as: Comp = "div", children, style = {}, ...props }, forwardedRef) {
  let { disabled, orientation, rangeStyle } = useSliderContext("SliderRange");
  return /* @__PURE__ */ React.createElement(Comp, {
    ref: forwardedRef,
    style: { position: "absolute", ...rangeStyle, ...style },
    ...props,
    "data-reach-slider-range": "",
    "data-disabled": disabled ? "" : void 0,
    "data-orientation": orientation
  });
});
SliderRangeImpl.displayName = "SliderRange";
var SliderRange2 = React.memo(SliderRangeImpl);
var SliderTrackHighlightImpl = React.forwardRef(function SliderTrackHighlightImpl2(props, ref) {
  if (true) {
    React.useEffect(() => {
      console.warn("`SliderTrackHighlight` has been deprecated in favor of `SliderRange` and will be dropped from a future version of Reach UI.");
    }, []);
  }
  return /* @__PURE__ */ React.createElement(SliderRangeImpl, {
    "data-reach-slider-track-highlight": "",
    ...props,
    ref
  });
});
SliderTrackHighlightImpl.displayName = "SliderTrackHighlight";
var SliderTrackHighlight = React.memo(SliderTrackHighlightImpl);
SliderRange2.displayName = "SliderRange";
var SliderHandleImpl = React.forwardRef(function SliderHandle({
  as: Comp = "div",
  onBlur,
  onFocus,
  style = {},
  onKeyDown,
  ...props
}, forwardedRef) {
  const {
    ariaLabel,
    ariaLabelledBy,
    ariaValueText,
    disabled,
    handlePosition,
    handleRef,
    isVertical,
    handleKeyDown,
    orientation,
    setHasFocus,
    sliderMin,
    sliderMax,
    value
  } = useSliderContext("SliderHandle");
  const ref = useComposedRefs(handleRef, forwardedRef);
  return /* @__PURE__ */ React.createElement(Comp, {
    "aria-disabled": disabled || void 0,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabel ? void 0 : ariaLabelledBy,
    "aria-orientation": orientation,
    "aria-valuemax": sliderMax,
    "aria-valuemin": sliderMin,
    "aria-valuenow": value,
    "aria-valuetext": ariaValueText,
    role: "slider",
    tabIndex: disabled ? -1 : 0,
    ...props,
    "data-reach-slider-handle": "",
    ref,
    onBlur: composeEventHandlers(onBlur, () => {
      setHasFocus(false);
    }),
    onFocus: composeEventHandlers(onFocus, () => {
      setHasFocus(true);
    }),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
    style: {
      position: "absolute",
      ...isVertical ? { bottom: handlePosition } : { left: handlePosition },
      ...style
    }
  });
});
SliderHandleImpl.displayName = "SliderHandle";
var SliderHandle2 = React.memo(SliderHandleImpl);
SliderHandle2.displayName = "SliderHandle";
var SliderMarkerImpl = React.forwardRef(function SliderMarker({ as: Comp = "div", children, style = {}, value, ...props }, forwardedRef) {
  const {
    disabled,
    isVertical,
    orientation,
    sliderMin,
    sliderMax,
    value: sliderValue
  } = useSliderContext("SliderMarker");
  let inRange = !(value < sliderMin || value > sliderMax);
  let absoluteStartPosition = `${valueToPercent(value, sliderMin, sliderMax)}%`;
  let state = value < sliderValue ? "under-value" : value === sliderValue ? "at-value" : "over-value";
  return inRange ? /* @__PURE__ */ React.createElement(Comp, {
    ref: forwardedRef,
    style: {
      position: "absolute",
      ...isVertical ? { bottom: absoluteStartPosition } : { left: absoluteStartPosition },
      ...style
    },
    ...props,
    "data-reach-slider-marker": "",
    "data-disabled": disabled ? "" : void 0,
    "data-orientation": orientation,
    "data-state": state,
    "data-value": value,
    children
  }) : null;
});
SliderMarkerImpl.displayName = "SliderMarker";
var SliderMarker2 = React.memo(SliderMarkerImpl);
SliderMarker2.displayName = "SliderMarker";
function clamp(val, min, max) {
  return val > max ? max : val < min ? min : val;
}
function getDecimalPrecision(num) {
  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split("e-");
    const matissaDecimalPart = parts[0].split(".")[1];
    return (matissaDecimalPart ? matissaDecimalPart.length : 0) + parseInt(parts[1], 10);
  }
  const decimalPart = num.toString().split(".")[1];
  return decimalPart ? decimalPart.length : 0;
}
function percentToValue(percent, min, max) {
  return (max - min) * percent + min;
}
function roundValueToStep(value, step, min) {
  let nearest = Math.round((value - min) / step) * step + min;
  return Number(nearest.toFixed(getDecimalPrecision(step)));
}
function getPointerPosition(event, touchId) {
  if (touchId.current !== void 0 && event.changedTouches) {
    for (let i = 0; i < event.changedTouches.length; i += 1) {
      const touch = event.changedTouches[i];
      if (touch.identifier === touchId.current) {
        return {
          x: touch.clientX,
          y: touch.clientY
        };
      }
    }
    return false;
  }
  return {
    x: event.clientX,
    y: event.clientY
  };
}
function getNewValue(handlePosition, track, props) {
  let { orientation, min, max, step } = props;
  if (!track || !handlePosition) {
    return null;
  }
  let { left, width, bottom, height } = track.getBoundingClientRect();
  let isVertical = orientation === "vertical" /* Vertical */;
  let diff = isVertical ? bottom - handlePosition.y : handlePosition.x - left;
  let percent = diff / (isVertical ? height : width);
  let newValue = percentToValue(percent, min, max);
  return clamp(step ? roundValueToStep(newValue, step, min) : newValue, min, max);
}
function useDimensions(ref) {
  const [{ width, height }, setDimensions] = React.useState({
    width: 0,
    height: 0
  });
  useLayoutEffect(() => {
    let ownerDocument = getOwnerDocument(ref.current);
    let ownerWindow = ownerDocument.defaultView || window;
    if (ref.current) {
      const { height: _newHeight, width: _newWidth } = ownerWindow.getComputedStyle(ref.current);
      let newHeight = parseFloat(_newHeight);
      let newWidth = parseFloat(_newWidth);
      if (newHeight !== height || newWidth !== width) {
        setDimensions({ height: newHeight, width: newWidth });
      }
    }
  }, [ref, width, height]);
  return { ref, width, height };
}
function valueToPercent(value, min, max) {
  return (value - min) * 100 / (max - min);
}
export {
  SLIDER_HANDLE_ALIGN_CENTER,
  SLIDER_HANDLE_ALIGN_CONTAIN,
  SLIDER_ORIENTATION_HORIZONTAL,
  SLIDER_ORIENTATION_VERTICAL,
  Slider,
  SliderHandle2 as SliderHandle,
  SliderHandleAlignment,
  SliderInput,
  SliderMarker2 as SliderMarker,
  SliderOrientation,
  SliderRange2 as SliderRange,
  SliderTrack2 as SliderTrack,
  SliderTrackHighlight
};
